import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ExternalLink, Pencil, RefreshCw, Sparkles } from 'lucide-react';
import { ReactNode, useState } from 'react';
import { useUser } from '../../app/session';
import { toast } from '../../components/toasts';
import { Badge, Button, Input } from '../../components/ui';
import { errorMessage, hasPermission, patch, post } from '../../lib/api';
import { CLOSE_REASON_LABEL, dateTimeOf, formatDuration, formatPhone, SYNC_STATUS_LABEL, SYNC_TYPE_LABEL, timeOf } from '../../lib/format';
import { useAttendanceSettings, useContact, useCustomerShortcuts, useContactCalls, useContactHistory, useSyncTasks, useTicketLink, ticketLabel } from '../../lib/queries';
import { AttendanceDetails } from '../../lib/types';
import { ContactTimeline } from './ContactTimeline';
import { TicketDecisionModal } from './TicketDecisionModal';

const DECISION_LABEL: Record<string, string> = {
  pending: 'não definido',
  reuse: 'chamado existente',
  create: 'novo chamado',
  deferred: 'decidir depois',
  none: 'sem chamado (spam)',
};

function Section({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="border-b border-border px-4 py-3">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-xs font-semibold tracking-wide text-fg-soft uppercase">{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

/** Resumo do histórico do cliente pela IA (sob demanda: cada pedido gasta tokens). */
function HistorySummary({ contactId, attendanceId }: { contactId: string; attendanceId: string }) {
  const [result, setResult] = useState<{ summary: string; generatedAt: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const generate = async () => {
    setLoading(true);
    try {
      setResult(await post<{ summary: string; generatedAt: string }>('/insights/customer-history', { contactId, attendanceId }));
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setLoading(false);
    }
  };
  if (!result) {
    return (
      <Button size="sm" variant="ghost" className="mb-2" loading={loading} onClick={() => void generate()} title="A IA lê os atendimentos e ligações anteriores e resume o que o cliente já tratou">
        <Sparkles className="size-3.5" /> Resumir o histórico com IA
      </Button>
    );
  }
  return (
    <div className="mb-3 rounded-md bg-accent/5 p-2 text-xs">
      <div className="mb-1 flex items-center gap-1.5 font-medium">
        <Sparkles className="size-3.5 text-accent" /> Histórico do cliente (IA)
      </div>
      <p className="whitespace-pre-line text-fg">{result.summary}</p>
      <div className="mt-1 flex items-center justify-between text-[11px] text-fg-soft">
        <span>gerado às {timeOf(result.generatedAt)}; confira na linha do tempo</span>
        <button className="text-accent" disabled={loading} onClick={() => void generate()}>
          gerar de novo
        </button>
      </div>
    </div>
  );
}

function timelineTitle(attendances: number, calls: number): string {
  const parts = [attendances > 1 ? `${attendances} atendimentos` : '', calls ? (calls === 1 ? '1 ligação' : `${calls} ligações`) : ''].filter(Boolean);
  return parts.length ? `Linha do tempo (${parts.join(', ')})` : 'Linha do tempo';
}

/** `className` troca a coluna fixa (computador) por outra disposição (tela por cima, no celular). */
export function SidePanel({ attendance: a, className }: { attendance: AttendanceDetails; className?: string }) {
  const user = useUser();
  const qc = useQueryClient();
  const contact = useContact(a.contactId).data;
  const shortcuts = useCustomerShortcuts(a.contactId, contact?.agidesk.customer?.id ?? null).data;
  const sync = useSyncTasks(a.id).data ?? [];
  const history = useContactHistory(a.contactId).data ?? [];
  const calls = useContactCalls(a.contactId).data ?? [];
  const ticketLink = useTicketLink(a.ticketId);
  const aiHistory = Boolean(useAttendanceSettings().data?.ai?.customerHistory);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [deciding, setDeciding] = useState(false);
  const mine = a.operatorId === user.id && a.status === 'em_atendimento';
  const canDecide = (mine || (a.status === 'finalizado' && a.ticketDecision === 'deferred')) && a.ticketDecision !== 'none' && !(a.ticketDecision === 'create' && a.ticketId);

  const linked = Boolean(contact?.agidesk.contactId);
  const [refreshing, setRefreshing] = useState(false);
  /** Relê nome, e-mail e cliente do cadastro do Agidesk (a tela atualiza pelo aviso em tempo real). */
  const refreshFromAgidesk = async () => {
    setRefreshing(true);
    try {
      await post(`/contacts/${a.contactId}/agidesk-lookup`);
      toast('Atualizando o contato com o cadastro do Agidesk…', 'info');
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setTimeout(() => setRefreshing(false), 1500);
    }
  };

  const saveContact = async () => {
    try {
      // Vinculado: o e-mail é do Agidesk (altera lá e usa "Atualizar do Agidesk").
      await patch(`/contacts/${a.contactId}`, { name: name.trim() || undefined, email: linked ? undefined : email.trim() || undefined });
      setEditing(false);
      void qc.invalidateQueries({ queryKey: ['contact', a.contactId] });
      void qc.invalidateQueries({ queryKey: ['attendance', a.id] });
      toast('Contato atualizado', 'ok');
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  };

  return (
    <aside className={clsx('overflow-y-auto bg-panel', className ?? 'w-80 shrink-0 border-l border-border')}>
      <Section
        title="Contato"
        action={
          hasPermission(user, 'contact:update') && contact && !editing ? (
            <button
              className="text-fg-soft hover:text-fg"
              onClick={() => {
                setName(contact.name ?? '');
                setEmail(contact.email);
                setEditing(true);
              }}
              aria-label="Editar contato"
            >
              <Pencil className="size-3.5" />
            </button>
          ) : null
        }
      >
        {contact && !editing && (
          <div className="space-y-1 text-sm">
            <div className="font-medium">{contact.name ?? '—'}</div>
            <div className="text-fg-soft">{contact.phones.map(formatPhone).join(', ')}</div>
            <div className="break-all text-fg-soft">
              {contact.email} {contact.emailIsPlaceholder && <Badge>técnico</Badge>}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {linked ? <Badge tone="ok">no Agidesk (#{contact.agidesk.contactId})</Badge> : <Badge tone="warn">ainda não está no Agidesk</Badge>}
              {linked && hasPermission(user, 'contact:update') && (
                <button
                  className="inline-flex items-center gap-1 text-xs text-accent hover:underline disabled:opacity-50"
                  onClick={() => void refreshFromAgidesk()}
                  disabled={refreshing}
                  title="Relê nome, e-mail e cliente do cadastro do contato no Agidesk"
                >
                  <RefreshCw className={clsx('size-3', refreshing && 'animate-spin')} /> Atualizar do Agidesk
                </button>
              )}
            </div>
          </div>
        )}
        {editing && (
          <div className="space-y-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome" />
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-mail" disabled={linked} title={linked ? 'E-mail do cadastro do Agidesk' : undefined} />
            <p className="text-[11px] text-fg-soft">
              {linked
                ? 'Contato vinculado: o e-mail vem do Agidesk. Altere por lá e use "Atualizar do Agidesk" (também atualiza sozinho quando o cliente manda mensagem).'
                : 'Trocar o e-mail refaz a busca do contato no Agidesk.'}
            </p>
            <div className="flex gap-2">
              <Button size="sm" variant="primary" onClick={() => void saveContact()}>
                Salvar
              </Button>
              <Button size="sm" onClick={() => setEditing(false)}>
                Cancelar
              </Button>
            </div>
          </div>
        )}
      </Section>

      <Section title="Cliente">
        <div className="text-sm">{contact?.agidesk.customer ? contact.agidesk.customer.name : <span className="text-warn">não definido</span>}</div>
        {shortcuts?.uniplusWeb && (
          <a
            href={shortcuts.uniplusWeb}
            target="_blank"
            rel="noreferrer"
            className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-border bg-muted px-2.5 py-1 text-xs font-medium hover:border-accent hover:text-accent"
            title={shortcuts.uniplusWeb}
          >
            <ExternalLink className="size-3.5" /> Acessar o Uniplus Web
          </a>
        )}
      </Section>

      <Section
        title="Chamado"
        action={
          canDecide ? (
            <Button size="sm" onClick={() => setDeciding(true)}>
              {a.ticketDecision === 'pending' || a.ticketDecision === 'deferred' ? 'Definir' : 'Alterar'}
            </Button>
          ) : null
        }
      >
        <div className="space-y-1 text-sm">
          <div>
            {a.ticketId && ticketLink ? (
              <a href={ticketLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-semibold text-accent hover:underline">
                {ticketLabel(a)} <ExternalLink className="size-3.5" />
              </a>
            ) : a.ticketId ? (
              <b>{ticketLabel(a)}</b>
            ) : (
              <span className="text-fg-soft">{a.ticketDecision === 'create' ? 'sendo criado no Agidesk…' : 'sem número'}</span>
            )}
          </div>
          <div className="text-fg-soft">{DECISION_LABEL[a.ticketDecision] ?? a.ticketDecision}</div>
          {a.ticketRequest && <div className="text-xs text-fg-soft">Assunto: {a.ticketRequest.title}</div>}
        </div>
      </Section>

      <Section title="Tempo efetivo">
        <div className="mb-2 font-mono text-lg">{formatDuration(a.totalSeconds)}</div>
        <div className="space-y-1 text-xs text-fg-soft">
          {a.sessions.length === 0 && <div>Nenhuma sessão ainda.</div>}
          {a.sessions.slice(-8).map((s) => (
            <div key={s.id} className="flex justify-between gap-2">
              <span>
                {timeOf(s.startedAt)}–{s.finishedAt ? timeOf(s.finishedAt) : 'agora'} {s.operatorName && s.operatorId !== user.id ? `· ${s.operatorName}` : ''}
              </span>
              <span title={s.closeReason ? CLOSE_REASON_LABEL[s.closeReason] : 'aberta'}>{s.durationSeconds !== null ? formatDuration(s.durationSeconds) : '…'}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section title={timelineTitle(history.length, calls.length)}>
        {aiHistory && (history.length > 1 || calls.length > 0) && <HistorySummary key={a.id} contactId={a.contactId} attendanceId={a.id} />}
        {history.length > 1 || calls.length > 0 ? (
          <div className="max-h-80 overflow-y-auto pr-1">
            <ContactTimeline history={history} calls={calls} currentId={a.id} />
          </div>
        ) : (
          <p className="text-xs text-fg-soft">Primeiro atendimento deste contato.</p>
        )}
      </Section>

      {sync.length > 0 && (
        <Section title="Agidesk">
          <div className="space-y-2 text-sm">
            {sync.map((t) => (
              <div key={t.id}>
                <div className="flex items-center justify-between gap-2">
                  <span>{SYNC_TYPE_LABEL[t.type] ?? t.type}</span>
                  <Badge tone={t.status === 'done' ? 'ok' : t.status === 'waiting' || t.status === 'failed' ? 'danger' : 'neutral'}>{SYNC_STATUS_LABEL[t.status] ?? t.status}</Badge>
                </div>
                {t.lastError && t.status !== 'done' && <p className="mt-0.5 text-xs text-danger">{t.lastError}</p>}
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section title="Atendimento">
        <div className="space-y-1 text-xs text-fg-soft">
          <div>Canal: {a.channelName}</div>
          {a.queuedAt && <div>Entrou na fila: {dateTimeOf(a.queuedAt)}</div>}
          {a.takenAt && <div>Assumido: {dateTimeOf(a.takenAt)}</div>}
          {a.finishedAt && <div>Finalizado: {dateTimeOf(a.finishedAt)}</div>}
          {a.summary && <div className="whitespace-pre-wrap text-fg">Resumo: {a.summary}</div>}
        </div>
      </Section>

      {deciding && (
        <TicketDecisionModal
          attendanceId={a.id}
          // Finalizado com "decidir depois": o resumo ainda pode ser revisto (ou gerado pela IA) junto com o chamado.
          pendingSummary={a.status === 'finalizado' && a.ticketDecision === 'deferred' ? (a.summary ?? '') : undefined}
          onClose={() => setDeciding(false)}
        />
      )}
    </aside>
  );
}
